<template>
	<div>
		<div
			class="user-info-container"
			v-if="userInfo"
		>
			<p class="text-container">
				Hi,
				<span
					class="cobrowsing-censor"
					:style="{ marginLeft: '5px' }"
					>{{ employeeName }}</span
				>
			</p>
			<FloatingMenu closeOnClick>
				<template #action-button>
					<!--<i class="fas fa-cog" />-->
					<i class="fas fa-bars" />
				</template>

				<template #body>
					<div
						class="menu-list"
						@click="openSettingModal()"
					>
						<div class="inner-container">
							<span class="fa-stack fa-1x">
								<i class="fas fa-user-cog fa-stack-1x menu-icon" />
							</span>
							<span class="option-text">User Settings</span>
						</div>
					</div>
					<div
						class="menu-list"
						@click="openTicketModal()"
						v-if="user.hasAccessToPermission(EmployeePermissionsType.AccessCashBash)"
					>
						<div class="inner-container">
							<span class="fa-stack fa-1x">
								<i class="fas fa-ticket-alt fa-stack-1x menu-icon" />
							</span>
							<span class="option-text">Cash Bash</span>
						</div>
					</div>
					<div
						class="menu-list"
						@click="openPlayCashBash()"
						v-if="user.isAdmin"
					>
						<div class="inner-container">
							<span class="fa-stack fa-1x">
								<i class="fas fa-ticket-alt fa-stack-1x menu-icon" />
							</span>
							<span class="option-text">Play Cash Bash</span>
						</div>
					</div>
					<div class="menu-list">
						<div
							class="inner-container"
							@click="logout()"
						>
							<span class="fa-stack fa-1x">
								<i class="fas fa-sign-out-alt fa-stack-1x menu-icon" />
							</span>
							<span class="option-text">Logout</span>
						</div>
					</div>
				</template>
			</FloatingMenu>
		</div>
		<PanelESign
			v-if="userInfo && isESignModalOpen"
			:name="employeeName"
			:userInfo="employeeInfo"
			:cancelHandler="!needFirstSign ? cancelSignature : null"
			:submitHandler="submitSignature"
		/>
	</div>
</template>

<script setup lang="ts">
	import { ref, computed, onMounted, Ref } from 'vue';
	import $modal from '@core/services/modal';
	import { AccessEmployeeClass } from '@core/classes/AccessEmployeeClass';
	import api from '@core/services/api/_api';
	import auth from '@core/services/auth';
	import { dateInFuture } from '@core/services/custom-validators';
	import { EmployeePermissionsType } from '@core/classes/SharedEnums';
	import { useCurrentUser } from '@core/stores/CurrentUserAuth';
	import { useRouter } from 'vue-router';
	import util from '@core/services/util';

	import { DigitalSignatureData } from '@core/classes/Paperwork/DigitalSignatureData';
	import FloatingMenu from '@/components/FloatingMenu.vue';
	import modalTicket from '@/modals/modalTicket.vue';
	import modalUserSettings from '@/modals/modalUserSettings.vue';
	import PanelESign from '@core/components/PanelESign.vue';

	const router = useRouter();
	const currentUser = useCurrentUser();

	// State
	const today = ref(null);
	const userInfo = ref<AccessEmployeeClass>(null) as Ref<AccessEmployeeClass>;
	const needFirstSign = ref(false);
	const notaryInfoUpdate = ref([]);
	const isESignModalOpen = ref<boolean>(false);

	// Computed
	const employeeName = computed(() => {
		if (!userInfo.value?.employeeName.includes(',')) return userInfo.value?.employeeName;

		const names = userInfo.value?.employeeName.split(',');
		if (names.length > 1) return `${names[1]} ${names[0]}`;

		return userInfo.value?.employeeName;
	});

	const user = computed(() => auth.getTokenPayload());

	const employeeInfo = computed(() => {
		return userInfo.value
			? {
					id: userInfo.value.id,
					storeCode: userInfo.value.employeeAccess.storeCodes[0],
					cellNumber: userInfo.value.employeeWorkNumber ?? userInfo.value.employeePhoneNumber,
				}
			: null;
	});

	// Methods
	const setUserInfo = async () => {
		try {
			const payload = auth.getTokenPayload();
			const { data } = await api.settings.getUserById(payload.id);
			const userInfoTemp = new AccessEmployeeClass(data);
			if (
				util.isNullOrEmpty(userInfoTemp.employeeSignature) ||
				util.isNullOrEmpty(userInfoTemp.employeeInitials) ||
				util.isNullOrEmpty(userInfoTemp.signatureData) ||
				util.isNullOrEmpty(userInfoTemp.initialsData)
			) {
				needFirstSign.value = true;
				isESignModalOpen.value = true;
			} else {
				isESignModalOpen.value = false;
			}

			userInfo.value = userInfoTemp;

			currentUser.setUserInfo(userInfoTemp);

			// Check if notary settings need to be updated
			if (util.isNull(userInfoTemp.isNotary) && userInfoTemp.hasAccessToPermission(EmployeePermissionsType.CanNotarize)) {
				notaryInfoUpdate.value.push({
					message: 'Please set your notary information.',
				});
			}
			if (userInfoTemp.userNotaryInformation && userInfoTemp.hasAccessToPermission(EmployeePermissionsType.CanNotarize)) {
				const expiredNotaryInfo = userInfoTemp.userNotaryInformation?.filter(n => {
					return !dateInFuture(n.expirationDate, new Date());
				});
				if (expiredNotaryInfo.length > 0) {
					notaryInfoUpdate.value.push({
						message: 'Your notary information has expired please update or remove.',
						expiredNotaryInfo: expiredNotaryInfo,
					});
				}
			}

			if (notaryInfoUpdate.value?.length > 0) {
				openSettingModal();
			}
		} catch (error) {
			console.error(error);
			util.toastr('error', 'Get User By Code', error);
		}
	};

	const logout = () => {
		localStorage.setItem('Saturn-Theme', '');
		auth.logout();
	};

	const openPlayCashBash = () => {
		router.push(`/cashbash/`);
	};

	const openSettingModal = () => {
		$modal.open(modalUserSettings, {
			name: 'modalUserSettings',
			passedData: {
				userInfo: userInfo.value,
				notaryInfoUpdate: notaryInfoUpdate.value,
				toggleESignModal: () => {
					isESignModalOpen.value = !isESignModalOpen.value;
				},
			},
			postFunction: (user: AccessEmployeeClass) => {
				currentUser.setUserInfo(user);
				userInfo.value = user;
				notaryInfoUpdate.value = [];
			},
			backdrop: false,
		});
	};

	const openTicketModal = () => {
		$modal.open(modalTicket, {
			name: 'modalTicket',
			passedData: { userInfo: userInfo.value },
			backdrop: false,
		});
	};

	const submitSignature = (signature: HTMLCanvasElement, initials: HTMLCanvasElement, signatureData: DigitalSignatureData, initialsData: DigitalSignatureData) => {
		userInfo.value.employeeSignature = typeof signature == 'string' ? signature : signature.toDataURL();
		userInfo.value.employeeInitials = typeof initials == 'string' ? initials : initials.toDataURL();
		userInfo.value.signatureData = signatureData;
		userInfo.value.initialsData = initialsData;

		if (needFirstSign.value) {
			updateSignature();
		} else {
			isESignModalOpen.value = false;
		}
	};

	const updateSignature = async () => {
		try {
			await api.users.setUser(userInfo.value);
			util.toastr('success', 'User First Signature', 'Successfully Created');
			needFirstSign.value = false;
			isESignModalOpen.value = false;
			$modal.close();
		} catch (error) {
			util.toastr('error', 'User First Signature', error);
		}
	};

	const cancelSignature = () => {
		isESignModalOpen.value = false;
	};

	// Lifecycle
	onMounted(async () => {
		const todayDate = new Date();
		const dd = String(todayDate.getDate()).padStart(2, '0');
		const mm = String(todayDate.getMonth() + 1).padStart(2, '0'); //January is 0!
		const yyyy = todayDate.getFullYear();

		today.value = mm + '/' + dd + '/' + yyyy;

		await setUserInfo();
	});
</script>
